// Layout colonne persistito (round 3 punto 1): ordine (drag dell'header),
// larghezze (drag del bordo) e visibilità (menu Colonne) in localStorage —
// sopravvive a reload e sessioni, per-browser.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ColumnDef } from "./components/columns";

const STORAGE_KEY = "om.fatturazione.columns.v1";
const MIN_WIDTH = 56;

interface StoredLayout {
  order: string[];
  widths: Record<string, number>;
  hidden: string[];
}

function loadStored(): StoredLayout | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const p = parsed as Partial<StoredLayout>;
    return {
      order: Array.isArray(p.order) ? p.order.filter((k) => typeof k === "string") : [],
      widths: typeof p.widths === "object" && p.widths !== null ? p.widths : {},
      hidden: Array.isArray(p.hidden) ? p.hidden.filter((k) => typeof k === "string") : [],
    };
  } catch {
    return null;
  }
}

/** Ordine salvato ∩ colonne esistenti + colonne NUOVE inserite alla loro
 *  posizione di default (un deploy che aggiunge colonne non rompe il layout). */
function mergeOrder(defs: ColumnDef[], saved: string[]): string[] {
  const known = new Set(defs.map((d) => d.key));
  const kept = saved.filter((k) => known.has(k));
  const keptSet = new Set(kept);
  const result = [...kept];
  defs.forEach((d, i) => {
    if (keptSet.has(d.key)) return;
    const anchor = defs
      .slice(0, i)
      .reverse()
      .find((prev) => keptSet.has(prev.key));
    const at = anchor ? result.indexOf(anchor.key) + 1 : i === 0 ? 0 : result.length;
    result.splice(at, 0, d.key);
    keptSet.add(d.key);
  });
  return result;
}

export function useColumnLayout(defs: ColumnDef[]) {
  const defMap = useMemo(() => new Map(defs.map((d) => [d.key, d])), [defs]);
  const [order, setOrder] = useState<string[]>(() => {
    const saved = loadStored();
    return saved?.order.length ? mergeOrder(defs, saved.order) : defs.map((d) => d.key);
  });
  const [widths, setWidths] = useState<Record<string, number>>(() => loadStored()?.widths ?? {});
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(loadStored()?.hidden ?? []));
  // Ref per non far partire il drag-riordino mentre si trascina il bordo.
  const resizingRef = useRef(false);
  const dragKeyRef = useRef<string | null>(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ order, widths, hidden: [...hidden] } satisfies StoredLayout),
      );
    } catch {
      // storage pieno/negato: layout solo in-memoria, nessun errore utente
    }
  }, [order, widths, hidden]);

  const visible = useMemo(
    () =>
      order
        .map((k) => defMap.get(k))
        .filter((d): d is ColumnDef => d !== undefined && !hidden.has(d.key)),
    [order, hidden, defMap],
  );

  const widthOf = useCallback(
    (key: string) => Math.max(MIN_WIDTH, widths[key] ?? defMap.get(key)?.width ?? 120),
    [widths, defMap],
  );

  /** mousedown sul bordo destro dell'header: trascina per ridimensionare. */
  const startResize = useCallback(
    (key: string, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      resizingRef.current = true;
      const startX = e.clientX;
      const startW = widthOf(key);
      const onMove = (ev: MouseEvent) => {
        const w = Math.max(MIN_WIDTH, startW + (ev.clientX - startX));
        setWidths((prev) => ({ ...prev, [key]: w }));
      };
      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        // al frame dopo: il click/dragstart sintetico post-resize va ignorato
        window.setTimeout(() => {
          resizingRef.current = false;
        }, 0);
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [widthOf],
  );

  const onDragStart = useCallback((key: string, e: React.DragEvent) => {
    if (resizingRef.current) {
      e.preventDefault();
      return;
    }
    dragKeyRef.current = key;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", key);
  }, []);

  const onDropOn = useCallback((targetKey: string, e: React.DragEvent) => {
    e.preventDefault();
    const dragged = dragKeyRef.current;
    dragKeyRef.current = null;
    if (!dragged || dragged === targetKey) return;
    setOrder((prev) => {
      const next = prev.filter((k) => k !== dragged);
      const at = next.indexOf(targetKey);
      if (at < 0) return prev;
      next.splice(at, 0, dragged);
      return next;
    });
  }, []);

  const toggleHidden = useCallback((key: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setOrder(defs.map((d) => d.key));
    setWidths({});
    setHidden(new Set());
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignorato: reset comunque applicato in-memoria
    }
  }, [defs]);

  return {
    visible,
    order,
    hidden,
    widthOf,
    startResize,
    onDragStart,
    onDropOn,
    toggleHidden,
    reset,
  };
}
